import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import {
  DropMenu,
  DropMenuTrigger,
  DatePicker,
} from '@tencent/spaui';
import classNames from 'classnames';
import moment from 'moment';
import _ from 'lodash';

import { RPCFactory } from '../../../utils/RequestUtil';
import ChatBox from './ChatBox';
import {
  BASE64_ROOM_AVATAR,
  BASE64_AVATAR,
} from './assets';
import { CorpSelector } from '../qw/corpSelector';

const EMPTY_OBJ = {};

const RECENT_MONTH = {
  start: moment().subtract(6, 'days').valueOf(),
  end: moment().valueOf(),
};

const getLastMessage = (data) => {
  const info = data.msgContent;
  const type = data.msgType;
  if  (type === 'text') {
    return info.content;
  } if (type === 'image') {
    return '[图片]';
  } if (type === 'emotion') {
    return '[表情]';
  } if (type === 'video') {
    return '[视频]';
  } if (type === 'file') {
    return '[文件]';
  } if (type === 'link') {
    return info.title;
  }
};

const SvgArror = (props) => (
  <svg {...props} width="10" height="17" viewBox="0 0 10 4" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" fill="#939599" d="M4.24074 6.1142C4.63984 6.57981 5.36016 6.57981 5.75926 6.1142L9.58504 1.65079C10.141 1.00212 9.68013 0 8.82578 0H1.17422C0.319868 0 -0.141042 1.00212 0.414964 1.65079L4.24074 6.1142Z" />
  </svg>
);

const SearchInput = (props) => {
  const ref = useRef();
  const [_value, setValue] = useState(props.value || '');
  const [chineseInput, setChineseInput] = useState(false);

  const handleInputChange = (event, signal) => {
    if (!signal && chineseInput) return;
    const { value } = event.target;
    setValue(value);
    if (typeof props.onChange !== 'function') return;
    props.onChange(value);
  };

  const handleChineseInputStart = () => {
    setChineseInput(true);
  };

  const handleChineseInputEnd = (event) => {
    setChineseInput(false);
    handleInputChange(event, true);
  };

  useEffect(() => {
    if (ref.current.value === props.value) return;
    ref.current.value = props.value
  }, [props.value]);

  return (
    <div className="custom-search-component">
      <i className="search-icon" />
      <input
        type="text"
        ref={ref}
        className="search-input"
        placeholder={props.placeholder}
        onCompositionStart={handleChineseInputStart}
        onCompositionEnd={handleChineseInputEnd}
        onChange={handleInputChange}
        defaultValue={_value}
      />
    </div>
  );
};

/**
 * 自定义员工选择下拉菜单
 */
const LinkComponent = (props) => {
  const { data, value, onClick } = props;
  const classname = classNames(
    'qca-filter-menu-name',
    { selected: value === data.value },
  )
  return (
    <div className="qca-filter-menu-item" onClick={onClick}>
      <div className="qca-filter-menu-avatar">
        {
          data.avatar && <img src={data.avatar} alt="头像" />
        }
      </div>
      <div className={classname}>{ data.label }</div>
    </div>
  );
};

const CustomerList = (props) => {
  const list = props.list || [];

  const getActiveClassName = (id) => classNames(
    'qca-customer-item',
    { active: id === props.activeCustomer },
  );

  const handleCustomerClick = (item) => {
    if (typeof props.onChange !== 'function') return;
    props.onChange(item);
  };

  return (
    <div className="qca-customers">
      {
        list.map((item, index) => {
          return (
            <div key={item.customerId || index} className={getActiveClassName(item.customerId)}>
              <div className="customer-content" onClick={() => handleCustomerClick(item)}>
                <div className="customer-avatar">
                  <img
                    alt="avatar"
                    width="40"
                    height="40"
                    src={item.isRoom ? BASE64_ROOM_AVATAR : item.avatar || BASE64_AVATAR}
                  />
                </div>
                <time className="customer-time">{ item.time }</time>
                <p className="customer-name">{ item.name }</p>
                <p className="customer-message">{ item.message }</p>
              </div>
            </div>
          );
        })
      }
    </div>
  );
};

const Archive = (props) => {
  /**
   * 页面状态：
   *   loading: 正在请求是否有访问权限
   *   loaded:  有权限访问
   *   noauth:  没有权限访问
   */
  const [pageStatus, setPageStatus] = useState('loading');
  const [employee, setEmployee] = useState('');
  const [employeeInfo, setEmployeeInfo] = useState(EMPTY_OBJ);
  const [employeeList, setEmployeeList] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  // 当前选中的需要查看聊天记录的客户的 id
  const [customer, setCustomer] = useState(EMPTY_OBJ);
  const [customerList, setCustomerList] = useState([]);
  const [searchDate, setSearchDate] = useState(RECENT_MONTH);
  const [customerInited, setCustomerInited] = useState(false);
  const getSecret = () => {
    RPCFactory.qwSystemConfig.getSecret().done((data) => {
      if (
        data.externalContactSecretSetStatus ===1
        && data.chatContentSecretSetStatus === 1
        && data.chatContentSecretSetStatus === 1
      ) {
        setPageStatus('loaded');
      } else {
        setPageStatus('noauth');
      }
    }).fail(() => {
      setPageStatus('noauth');
    });
  };
  useEffect(() => {
    getSecret();
  }, []);

  const displayCustomers = useMemo(() => {
    if (searchValue.length < 0) return customerList;
    const filterFn = (item) => {
      return (item.name || '').indexOf(searchValue) !== -1;
    };
    return customerList.filter(filterFn);
  }, [searchValue, customerList]);

  const handleSearchValueChange = (value) => {
    setSearchValue(value);
    setCustomer(EMPTY_OBJ);
  };

  const handleEmployeeChange = (e, value) => {
    // 需要手动加一行这个来关闭下拉菜单
    document.body.click();
    setEmployee(value.employee);
    setSearchDate(RECENT_MONTH);
    setCustomer(EMPTY_OBJ);
    setSearchValue('');
    employeeList.forEach((item) => {
      if (item.value !== value.employee) return;
      setEmployeeInfo(item);
    });
  };

  const handleCustomerChange = (item) => {
    setCustomer(item);
    setSearchDate(RECENT_MONTH);
  };

  const handleTimeRangeChange = (e, start, end) => {
    setSearchDate({ start, end });
  };

  const employeeMenu = (
    <DropMenu
      data={[{ name: 'employee', list: employeeList }]}
      value={{ employee }}
      onChange={handleEmployeeChange}
      LinkComponent={LinkComponent}
    />
  );

  useEffect(() => {
    const { body } = document;
    body.removeAttribute('style');
  }, []);

  useEffect(() => {
    if (pageStatus !== 'loaded') return;
    RPCFactory.qwArchive.getEmployeeList()
      .done((data) => {
        const rawList = _.get(data, 'chatList') || [];
        const list = rawList.map((obj) => _.assign(obj, {
          value: obj.wxWorkUserId,
          label: obj.wxWorkUserName,
        }));
        setEmployeeList(list);
        setEmployee(_.get(list, '[0].wxWorkUserId', ''));
        setEmployeeInfo(list[0]);
      })
      .fail((ret, msg) => window.alert(msg));
  }, [pageStatus]);

  useEffect(() => {
    const id = employeeInfo.wxWorkUserId;
    const { wxWorkUserId } = employeeInfo;
    if (!id) return;
    RPCFactory.qwArchive.getCustomerList({ wxWorkUserId })
      .done((data) => {
        const customerArr = data.chatList || [];
        const arr = customerArr.map((item) => {
          if (!item.isGroupChat) {
            const info = item.externalUserInfo;
            return {
              isRoom: false,
              customerId: info.externalUserId,
              name: info.userName || info.usrName,
              avatar: info.avatar,
              time: item.lastWxWorkMsgInfo.msgTime,
              message: getLastMessage(item.lastWxWorkMsgInfo),
            };
          }
            const info = item.roomInfo;
            // 群聊
            return {
              isRoom: true,
              customerId: info.roomId,
              name: info.roomName,
              avatar: '',
              time: item.lastWxWorkMsgInfo.msgTime,
              message: getLastMessage(item.lastWxWorkMsgInfo),
            };

        });
        setCustomerList(arr);
        // 先注释掉下面的，允许切换员工后都会选中第一个客户
        // if (customerInited || arr.length < 1) {
        //   setCustomer(EMPTY_OBJ);
        //   return;
        // }
        // 第一次进入页面默认选中第一个员工下的第一个客户
        setCustomerInited(true);
        setCustomer(_.get(arr, '[0]', {}));
      })
      .fail((ret, msg) => window.alert(msg));

    // 切换员工的时候先清空客户列表
    setCustomerList([]);
  }, [employeeInfo]);

  const chatQuery = useMemo(() => {
    return { time: searchDate };
  }, [searchDate]);

  if (pageStatus === 'loading') {
    return (
      <div className="qw-conversation-archive">
        <div className="qca-loading">加载中...</div>
      </div>
    );
  }

  if (pageStatus === 'noauth') {
    return (
      <div className="qw-conversation-archive">
        <div className="qca-no-auth">
          <span>功能升级中，如需使用请联系腾讯内部产品运营。</span>
        </div>
      </div>
    );
  }

  return (
    <main className="main" id="main">
      <CorpSelector {...props} onChange={getSecret}/>
      <div className="qw-conversation-archive loaded">
        <div className="qca-box-left">
          <div className="qca-title">员工客户</div>
          <div className="qca-filter">
            <div className="qca-filter-left">员工</div>
            <div className="qca-filter-right">
              <div className="qca-filter-menu-avatar">
                {
                  employeeInfo.avatar && (<img src={employeeInfo.avatar} alt="头像" />)
                }
              </div>
              <DropMenuTrigger
                dropMenu={employeeMenu}
                dropMenuWrapClassName="employee-menu"
              >
                <div className="qca-filter-name">{ employeeInfo.wxWorkUserName }</div>
              </DropMenuTrigger>
            </div>
            <SvgArror style={{ marginRight: 4 }} />
          </div>
          <div className="qca-search">
            <div className="qca-search-left">客户</div>
            <div className="qca-search-right">
              <SearchInput
                placeholder="请输入客户名称"
                onChange={handleSearchValueChange}
                value={searchValue}
              />
            </div>
          </div>
          <div className="qca-customer-container">
            <CustomerList
              list={displayCustomers}
              activeCustomer={customer.customerId}
              onChange={handleCustomerChange}
            />
          </div>
        </div>
        <div className="qca-box-right">
          <div className="qca-title">聊天记录</div>
          <div className="qca-filter">
            <div className="qca-filter-right">
              <DatePicker
                useShortcut
                useTimeStamp
                hideSubmitButton
                type="range"
                longestRange={365}
                horizontalPosition='left'
                currentTime={moment().format('YYYY/M/D')}
                style={{ boxSizing: 'border-box' }}
                onChange={handleTimeRangeChange}
                startDate={searchDate.start}
                endDate={searchDate.end}
              />
            </div>
          </div>
          <ChatBox
            employee={employee}
            customer={customer}
            query={chatQuery}
          />
        </div>
      </div>
    </main>
  );
};

export default Archive;
